import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import LegalLinks from '../../components/LegalLinks';
import TermsConsent, { GoogleConsentNote } from '../../components/TermsConsent';
import { toast } from 'sonner';
import { Briefcase, Mail, Lock, User, Phone, ArrowRight } from 'lucide-react';
import axios from 'axios';

const API_URL = '';

/**
 * Kick off Emergent-managed Google OAuth. Redirects the browser to
 * `https://auth.emergentagent.com/` which lands the user back at
 * `/career/dashboard#session_id=<sid>` after Google consent.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 * THIS BREAKS THE AUTH.
 */
function startGoogleSignIn(carryQuery) {
  const redirectUrl = window.location.origin + '/career/dashboard' + (carryQuery || '');
  window.location.href =
    'https://auth.emergentagent.com/?redirect=' + encodeURIComponent(redirectUrl);
}

/** Inline Google "G" glyph — no image dependency. */
function GoogleGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    </svg>
  );
}


export default function CareerLoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Preserve `role` and `app` across login/register → dashboard so deep links
  // from emails or shared URLs land the applicant on exactly the right context.
  const pendingRole = searchParams.get('role') || (() => {
    try { return localStorage.getItem('pending_apply_role'); } catch (e) { return null; }
  })();
  const pendingApp = searchParams.get('app');
  const nextDashboardUrl = (() => {
    const params = new URLSearchParams();
    if (pendingRole) params.set('role', pendingRole);
    if (pendingApp) params.set('app', pendingApp);
    const q = params.toString();
    return q ? `/career/dashboard?${q}` : '/career/dashboard';
  })();
  const [isLogin, setIsLogin] = useState(true);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        const response = await axios.post(`${API_URL}/api/career/auth/login`, {
          email: formData.email,
          password: formData.password
        });
        
        localStorage.setItem('career_token', response.data.token);
        localStorage.setItem('career_user', JSON.stringify(response.data.user));
        toast.success('Login successful!');
        navigate(nextDashboardUrl);
      } else {
        if (formData.password !== formData.confirmPassword) {
          toast.error('Passwords do not match');
          setLoading(false);
          return;
        }
        if (!agreeTerms) {
          toast.error('Please agree to the Terms of Use and Privacy Policy');
          setLoading(false);
          return;
        }
        
        const response = await axios.post(`${API_URL}/api/career/auth/register`, {
          full_name: formData.full_name,
          email: formData.email,
          phone: formData.phone,
          password: formData.password,
          accepted_terms: agreeTerms
        });
        
        localStorage.setItem('career_token', response.data.token);
        localStorage.setItem('career_user', JSON.stringify(response.data.user));
        toast.success('Registration successful!');
        navigate(nextDashboardUrl);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // MOBILE VIEW
  // ============================================
  if (isMobile) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50" data-testid="career-login-mobile">
        {/* Mobile Header */}
        <div className="px-4 pt-8 pb-4">
          <Link to="/careers" className="inline-flex items-center gap-2 text-sm text-neutral-600">
            ← Back to Careers
          </Link>
        </div>

        {/* Mobile Login/Register Form */}
        <div className="px-4 py-6">
          <div className="text-center mb-6">
            <div className="flex items-center justify-center gap-2 mb-4">
              <img src="/hed-icon.png" alt="Half Eaten Donut" className="w-8 h-8" />
              <img src="/girgit-logo.png" alt="Girgit" className="w-8 h-8" />
            </div>
            <h1 className="text-2xl font-bold text-[#1a1a2e] mb-2">Girgit</h1>
            <span className="inline-block text-xs bg-indigo-500 text-white px-3 py-1 rounded-full">Careers Portal</span>
            <p className="text-neutral-600 mt-4 text-sm px-4">
              {isLogin ? 'Sign in to track your applications' : 'Create an account to apply'}
            </p>
          </div>

          <div className="bg-white  shadow-lg border border-white/30 p-6">
            {/* Mobile Tab Toggle */}
            <div className="flex bg-neutral-100 rounded-xl p-1 mb-6">
              <button
                onClick={() => setIsLogin(true)}
                className={`flex-1 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                  isLogin ? 'bg-white text-[#1a1a2e] shadow-soft' : 'text-neutral-600'
                }`}
              >
                Sign In
              </button>
              <button
                onClick={() => setIsLogin(false)}
                className={`flex-1 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                  !isLogin ? 'bg-white text-[#1a1a2e] shadow-soft' : 'text-neutral-600'
                }`}
              >
                Sign Up
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {!isLogin && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">Full Name</label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-neutral-400" />
                      <input
                        type="text"
                        value={formData.full_name}
                        onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                        className="w-full pl-10 pr-4 py-3 border border-white/40 rounded-xl focus:ring-2 focus:ring-indigo-400/30 text-base"
                        placeholder="Your full name"
                        required
                        data-testid="career-fullname-input-mobile"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">Phone Number</label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-neutral-400" />
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="w-full pl-10 pr-4 py-3 border border-white/40 rounded-xl focus:ring-2 focus:ring-indigo-400/30 text-base"
                        placeholder="Your phone number"
                        data-testid="career-phone-input-mobile"
                      />
                    </div>
                  </div>
                </>
              )}

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">Email Address</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-neutral-400" />
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full pl-10 pr-4 py-3 border border-white/40 rounded-xl focus:ring-2 focus:ring-indigo-400/30 text-base"
                    placeholder="Your email"
                    required
                    data-testid="career-email-input-mobile"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-neutral-400" />
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="w-full pl-10 pr-4 py-3 border border-white/40 rounded-xl focus:ring-2 focus:ring-indigo-400/30 text-base"
                    placeholder="Your password"
                    required
                    data-testid="career-password-input-mobile"
                  />
                </div>
              </div>

              {!isLogin && (
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">Confirm Password</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-neutral-400" />
                    <input
                      type="password"
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                      className="w-full pl-10 pr-4 py-3 border border-white/40 rounded-xl focus:ring-2 focus:ring-indigo-400/30 text-base"
                      placeholder="Confirm password"
                      required
                      data-testid="career-confirm-password-input-mobile"
                    />
                  </div>
                </div>
              )}
              {!isLogin && <TermsConsent checked={agreeTerms} onChange={setAgreeTerms} testId="career-terms-consent-mobile" />}

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3.5 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-xl font-semibold hover:opacity-90 transition-all flex items-center justify-center gap-2 disabled:opacity-50 text-base mt-6"
              >
                {loading ? 'Please wait...' : isLogin ? 'Sign In' : 'Create Account'}
                {!loading && <ArrowRight className="w-5 h-5" />}
              </button>

              {/* Divider + Google sign-in (mobile) */}
              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-neutral-200"></div>
                </div>
                <div className="relative flex justify-center">
                  <span className="bg-white px-3 text-xs text-neutral-500 uppercase tracking-wider">or</span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => startGoogleSignIn(searchParams.toString() ? `?${searchParams.toString()}` : '')}
                className="w-full py-3 border border-neutral-200 rounded-xl bg-white hover:bg-neutral-50 flex items-center justify-center gap-3 text-[#1a1a2e] font-medium text-base transition-colors"
                data-testid="career-google-signin-mobile"
              >
                <GoogleGlyph />
                Continue with Google
              </button>
              <GoogleConsentNote />
            </form>
          </div>

          {/* Mobile Back to Home */}
          <div className="mt-6 text-center">
            <Link to="/" className="text-neutral-600 hover:text-[#1a1a2e] text-sm">
              ← Back to Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // ============================================
  // DESKTOP VIEW (Original - Unchanged)
  // ============================================
  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo/Header */}
        <div className="text-center mb-8">
          {/* HED | Girgit Branding */}
          <div className="flex items-center justify-center gap-3 mb-4">
            <img src="/hed-icon.png" alt="Half Eaten Donut" className="w-8 h-8" />
            <img src="/hed-logo-black.png" alt="Half Eaten Donut" className="h-10" />
            <div className="w-px h-8 bg-gray-300 mx-2"></div>
            <img src="/girgit-logo.png" alt="Girgit" className="w-8 h-8" />
            <span className="text-2xl font-bold text-[#1a1a2e]">Girgit</span>
          </div>
          <span className="text-xs bg-indigo-500 text-white px-3 py-1 rounded-full">Careers Portal</span>
          
          <p className="text-neutral-600 mt-4">
            {isLogin ? 'Sign in to view and track your applications' : 'Create an account to apply for open positions'}
          </p>
        </div>

        {/* Form Card */}
        <div className="bg-white  shadow-xl border border-white/30 p-8">
          <form onSubmit={handleSubmit} className="space-y-5">
            {!isLogin && (
              <>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">Full Name</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-neutral-400" />
                    <input
                      type="text"
                      value={formData.full_name}
                      onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                      className="w-full pl-10 pr-4 py-3 border border-white/40 rounded-xl focus:ring-2 focus:ring-indigo-400/30 focus:border-indigo-500"
                      placeholder="Enter your full name"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">Phone Number</label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-neutral-400" />
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full pl-10 pr-4 py-3 border border-white/40 rounded-xl focus:ring-2 focus:ring-indigo-400/30 focus:border-indigo-500"
                      placeholder="Enter your phone number"
                    />
                  </div>
                </div>
              </>
            )}

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">Email Address</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-neutral-400" />
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full pl-10 pr-4 py-3 border border-white/40 rounded-xl focus:ring-2 focus:ring-indigo-400/30 focus:border-indigo-500"
                  placeholder="Enter your email"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-neutral-400" />
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full pl-10 pr-4 py-3 border border-white/40 rounded-xl focus:ring-2 focus:ring-indigo-400/30 focus:border-indigo-500"
                  placeholder="Enter your password"
                  required
                />
              </div>
            </div>

            {!isLogin && (
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">Confirm Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-neutral-400" />
                  <input
                    type="password"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    className="w-full pl-10 pr-4 py-3 border border-white/40 rounded-xl focus:ring-2 focus:ring-indigo-400/30 focus:border-indigo-500"
                    placeholder="Confirm your password"
                    required
                  />
                </div>
              </div>
            )}
            {!isLogin && <TermsConsent checked={agreeTerms} onChange={setAgreeTerms} />}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-xl font-semibold hover:opacity-90 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {loading ? 'Please wait...' : isLogin ? 'Sign In' : 'Create Account'}
              {!loading && <ArrowRight className="w-5 h-5" />}
            </button>

            {/* Divider + Google sign-in (desktop) */}
            <div className="relative my-4">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-neutral-200"></div>
              </div>
              <div className="relative flex justify-center">
                <span className="bg-white px-3 text-xs text-neutral-500 uppercase tracking-wider">or</span>
              </div>
            </div>
            <button
              type="button"
              onClick={() => startGoogleSignIn(searchParams.toString() ? `?${searchParams.toString()}` : '')}
              className="w-full py-3 border border-neutral-200 rounded-xl bg-white hover:bg-neutral-50 flex items-center justify-center gap-3 text-[#1a1a2e] font-medium transition-colors"
              data-testid="career-google-signin"
            >
              <GoogleGlyph />
              Continue with Google
            </button>
            <GoogleConsentNote />
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => setIsLogin(!isLogin)}
              className="text-indigo-600 hover:text-indigo-700 font-medium"
            >
              {isLogin ? "Don't have an account? Sign Up" : "Already have an account? Sign In"}
            </button>
          </div>
        </div>

        {/* Back to Home */}
        <div className="mt-6 text-center">
          <Link to="/" className="text-neutral-600 hover:text-[#1a1a2e]">
            ← Back to Home
          </Link>
          <LegalLinks tone="light" className="mt-4" />
        </div>
      </div>
    </div>
  );
}
